import { isCefrLevel, type WordState } from "@rmnlng/shared";
import { ChevronRight, Loader2, Plus, Search, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { features } from "@/lib/features";
import { useT, type I18nKey } from "@/lib/i18n";
import { useInfiniteScrollSentinel } from "@/lib/useInfiniteScrollSentinel";
import { cn } from "@/lib/utils";
import { useWordsList } from "@/lib/words";

// Filter is a discriminated union rather than a flat string so we can
// distinguish "learning" (a WordState) from "A1" (a CEFR tag) at the chip
// level — they route through different query params on the API.
type Filter = { kind: "all" } | { kind: "state"; value: Exclude<WordState, "new"> } | { kind: "tag"; value: string };

// State chips (excluding the old "New" which wasn't meaningful — it just
// counted words without any progress, which is most of the vocabulary).
// Level chips are rendered dynamically from `totals.levels`.
const STATE_CHIPS: Array<{ state: Exclude<WordState, "new">; labelKey: I18nKey }> = [
  { state: "learning", labelKey: "common.learning" },
  { state: "known", labelKey: "common.known" },
];

const STATE_DOT: Record<WordState, string> = {
  known: "var(--good)",
  learning: "var(--accent)",
  new: "var(--text-muted)",
};

export function WordsList() {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const t = useT();

  // Filter state lives in the URL so deep-links from WordDetail
  // (`/words?tag=еда`) just work. Also makes the filter bookmarkable /
  // shareable without us writing any extra sync code.
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = readFilterFromUrl(searchParams);

  function setFilter(next: Filter): void {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete("state");
        p.delete("tag");
        if (next.kind === "state") p.set("state", next.value);
        if (next.kind === "tag") p.set("tag", next.value);
        return p;
      },
      { replace: true },
    );
  }

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(handle);
  }, [search]);

  const { data, isPending, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useWordsList({
    q: debounced || undefined,
    language: "RO",
    state: filter.kind === "state" ? filter.value : undefined,
    tag: filter.kind === "tag" ? filter.value : undefined,
  });

  // Flatten the infinite query's page buckets back into a single list for
  // rendering. Totals are the same across pages (server returns the full
  // aggregate regardless of offset), so we just read page[0].
  const items = data?.pages.flatMap((p) => p.items) ?? [];
  const totals = data?.pages[0]?.totals;

  // Pre-fetch the next page when the sentinel scrolls near the viewport.
  // Guard with `!isFetchingNextPage` so the observer doesn't re-fire while
  // a request is already in flight.
  const sentinelRef = useInfiniteScrollSentinel<HTMLDivElement>(fetchNextPage, !!hasNextPage && !isFetchingNextPage);

  // Is the active tag a topical (non-CEFR) tag? CEFR tags have their own
  // chip in the filter row; topical tags show as a separate "filter
  // applied" strip instead (because we don't want a chip-per-topic).
  const topicalTagFilter = filter.kind === "tag" && !isCefrLevel(filter.value) ? filter.value : null;

  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex items-start justify-between gap-3">
        <h1 className="text-[26px] font-bold tracking-[-0.02em]">{t("words.title")}</h1>
        {features.wordManualAdd && (
          <Link
            to="/words/new"
            className="inline-flex items-center gap-1 px-2 py-1 text-[14px] font-semibold text-[color:var(--accent)]"
          >
            <Plus className="size-4" />
            {t("words.add")}
          </Link>
        )}
      </div>

      <div className="text-[13px] text-text-3">
        {totals
          ? t("words.totals", {
              total: totals.total,
              known: totals.known,
              learning: totals.learning,
            })
          : "\u00A0"}
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-text-3" />
        <Input
          className="pl-11"
          placeholder={t("words.search_placeholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Filter chips — All, state chips (Learning/Known), then dynamic
          CEFR levels pulled from the dataset. Horizontally scrollable so
          more levels don't force the row to wrap. */}
      <div className="scrollbar-hide -mx-4 flex gap-1.5 overflow-x-auto px-4 pb-1">
        <FilterChip active={filter.kind === "all"} onClick={() => setFilter({ kind: "all" })}>
          {t("common.all")}
        </FilterChip>
        {STATE_CHIPS.map((c) => (
          <FilterChip
            key={c.state}
            active={filter.kind === "state" && filter.value === c.state}
            onClick={() => setFilter({ kind: "state", value: c.state })}
          >
            {t(c.labelKey)}
          </FilterChip>
        ))}
        {totals?.levels.map((lvl) => (
          <FilterChip
            key={lvl}
            active={filter.kind === "tag" && filter.value === lvl}
            onClick={() => setFilter({ kind: "tag", value: lvl })}
          >
            {lvl}
          </FilterChip>
        ))}
      </div>

      {/* Topical-tag filter strip — appears when a #tag was selected
          (e.g. from WordDetail). Clickable × clears. CEFR levels don't
          reach here; they're chips in the row above. */}
      {topicalTagFilter && (
        <div className="flex items-center gap-2 text-[12px] text-text-3">
          <span>{t("words.filter_by_tag")}</span>
          <span
            className="inline-flex items-center rounded-full px-2.5 py-0.5 font-medium"
            style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
          >
            #{topicalTagFilter}
          </span>
          <button
            type="button"
            onClick={() => setFilter({ kind: "all" })}
            className="inline-flex items-center gap-0.5 text-text-3 hover:text-text"
            aria-label={t("words.clear_filter")}
          >
            <X className="size-3.5" />
            {t("words.clear_filter")}
          </button>
        </div>
      )}

      {isError && (
        <p className="text-[13px] text-[color:var(--again)]">
          {error instanceof Error ? error.message : t("common.failed_to_load")}
        </p>
      )}

      {isPending ? (
        <WordsListSkeleton />
      ) : items.length === 0 ? (
        <EmptyState query={debounced} filter={filter} />
      ) : (
        <div>
          <div className="pb-1 pl-0.5 pt-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">
            {t("words.section_all")}
          </div>
          <ul className="flex flex-col">
            {items.map((w, i) => {
              const firstTranslation = w.translationsPreview[0] ?? null;
              const isLast = i === items.length - 1;
              return (
                <li key={w.id}>
                  <Link
                    to={`/words/${w.id}`}
                    className={cn("flex items-center gap-3 px-0.5 py-3", !isLast && "border-b border-border")}
                  >
                    <span
                      className="size-2 shrink-0 rounded-full"
                      style={{ background: STATE_DOT[w.state] }}
                      aria-label={w.state}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="truncate text-[16px] font-semibold text-text">{w.lemma}</span>
                        {w.pos && <span className="font-mono text-[11px] text-text-3">{w.pos.toLowerCase()}</span>}
                      </div>
                      {firstTranslation && (
                        <div className="mt-0.5 truncate text-[13px] text-text-2">
                          {firstTranslation}
                          {w.translationCount > 1 && ` +${w.translationCount - 1}`}
                        </div>
                      )}
                    </div>
                    {/* CEFR level — muted mono text in the right rail.
                        Neutral color by design; we can swap to a per-level
                        palette later without restyling the layout. */}
                    {w.level && (
                      <span className="inline-flex items-center gap-1 rounded-[4px] border border-transparent bg-[color:var(--surface-2)] px-2.5 py-0.5 font-mono text-[11px] font-medium tracking-[0.02em] text-text-3 transition-colors">
                        {w.level}
                      </span>
                    )}
                    <ChevronRight className="size-3.5 shrink-0 text-text-3" />
                  </Link>
                </li>
              );
            })}
          </ul>
          {/* Sentinel + loading indicator for infinite scroll. The sentinel
              is rendered unconditionally so the observer has a stable node
              to watch across re-renders; `hasNextPage` gates the effect
              inside the hook itself. */}
          <div ref={sentinelRef} aria-hidden className="h-1" />
          {isFetchingNextPage && (
            <div className="flex items-center justify-center gap-2 py-4 text-[12px] text-text-3">
              <Loader2 className="size-3.5 animate-spin" />
              {t("common.loading_more")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Turn the URL's ?state= / ?tag= params into a typed Filter. Unknown
// values fall through to "all" — a stale or hand-tampered URL gracefully
// shows everything rather than throwing.
function readFilterFromUrl(params: URLSearchParams): Filter {
  const tag = params.get("tag");
  if (tag) return { kind: "tag", value: tag };
  const state = params.get("state");
  if (state === "learning" || state === "known") return { kind: "state", value: state };
  return { kind: "all" };
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex shrink-0 items-center rounded-full px-3 py-1.5 text-[12px] font-medium whitespace-nowrap transition-colors",
        active
          ? "bg-[color:var(--accent-soft)] text-[color:var(--accent)]"
          : "border border-border text-text-2 hover:bg-[color:var(--surface-2)]",
      )}
    >
      {children}
    </button>
  );
}

function WordsListSkeleton() {
  const t = useT();
  return (
    <div>
      <div className="pb-1 pl-0.5 pt-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">
        {t("words.section_all")}
      </div>
      <ul className="flex flex-col">
        {Array.from({ length: 8 }).map((_, i) => (
          <li key={i} className={cn("flex items-center gap-3 px-0.5 py-3", i < 7 && "border-b border-border")}>
            <Skeleton className="size-2 shrink-0 rounded-full" />
            <div className="min-w-0 flex-1">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="mt-1.5 h-3 w-40" />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EmptyState({ query, filter }: { query: string; filter: Filter }) {
  const t = useT();
  // A short display label for the active filter, used inside the "no
  // words match {q} in {filter}" template. We only render the composed
  // form when the query has both; otherwise fall through to simpler text.
  const filterLabel =
    filter.kind === "state"
      ? filter.value === "learning"
        ? t("common.learning")
        : t("common.known")
      : filter.kind === "tag"
        ? `#${filter.value}`
        : "";
  return (
    <div className="rounded-[var(--r-lg)] border border-dashed border-border bg-surface/40 px-4 py-10 text-center text-[13px] text-text-3">
      {query ? (
        filter.kind !== "all" ? (
          t("words.empty_no_match_filtered", { q: query, filter: filterLabel })
        ) : (
          <>{t("words.empty_no_match", { q: query })}.</>
        )
      ) : filter.kind !== "all" ? (
        t("words.empty_filter")
      ) : (
        t("words.empty_all")
      )}
    </div>
  );
}
